import React, { useState, useEffect } from 'react'
import { useToast, Heading, Text, Box, Stack, CircularProgress, Button, Stat, StatLabel, StatNumber, Badge, Tag, TagLabel, FormControl, FormLabel, Input, FormHelperText } from '@chakra-ui/react'

import EmptyBox from '../../includes/EmptyBox'
import ParticipantMessenger from '../../includes/ParticipantMessenger'
import ParticipantNotes from '../../includes/ParticipantNotes'
import SimpleModal from '../../includes/SimpleModal'
import PhoneInput from '../../includes/PhoneInput'
import moment from 'moment'
import api from '../../../api'
import useStore from '../../../store'
import utils from '../../../utils/utils'

import FitbitImage from '../../../images/fitbit.png'
import GarminImage from '../../../images/garmin.png'

const SERVICES = {
  fitbit: { name: 'Fitbit', image: FitbitImage },
  garmin: { name: 'Garmin', image: GarminImage }
}

export default function Information ({ trialId, participantId, participant, setParticipant }) {
  const toast = useToast()
  const { user, trial } = useStore()

  function sendNotification () {
    const message = window.prompt('Write a message to send to the participant\'s device.')
    if (message) {
      api.trials.createParticipantNotifications(trialId, { message, participants: [participantId] }, () => {
        toast({ title: 'Notification sent', status: 'success' })
      }, err => toast({ title: 'Unable to notify participant', description: err.message, status: 'error' }))
    };
  }

  if (!participant) {
    return (
      <Box>
        <CircularProgress isIndeterminate />
      </Box>
    )
  }

  return (
    <Box>

      <Box display='flex' justifyContent='end'>
        {participant.pushAvailable && utils.canSupportTrial(user, trial) &&
          <Button size='sm' colorScheme='primary' onClick={sendNotification}>Send a notification</Button>}
      </Box>

      <Box display={{ md: 'flex' }} mt={5}>
        <Box w='50%' mr={5} borderWidth='1px' p={3} rounded='md'>
          <Box display='flex' justifyContent='end'>
            {utils.canWriteTrial(user, trial) &&
              <ParticipantEditor
                trigger={<Button size='sm' variant='outline' colorScheme='primary' onClick={() => {}}>Edit details</Button>}
                participant={participant}
                onChange={newP => setParticipant(newP)}
              />}
          </Box>
          <Stat>
            <StatLabel>General</StatLabel>
            <StatNumber>
              {participant.pushAvailable &&
                <Badge colorScheme='purple' variant='subtle' mr={1}>Push available</Badge>}
              <Badge colorScheme={participant.passwordSet ? 'green' : 'gray'} variant='subtle'>
                {participant.passwordSet ? 'Password set' : 'No password'}
              </Badge>
              {participant.email && <Badge colorScheme='green' variant='subtle' ml={1}>Email</Badge>}
              {participant.phone && <Badge colorScheme='green' variant='subtle' ml={1}>Phone</Badge>}
              {participant.whatsapp && <Badge colorScheme='green' variant='subtle' ml={1}>WhatsApp</Badge>}
            </StatNumber>
          </Stat>
          <Stat mt={2}>
            <StatLabel>Created</StatLabel>
            <StatNumber>{moment(participant.createdAt).format('DD/MM/YYYY HH:mm')}</StatNumber>
          </Stat>
          <Stat mt={2}>
            <StatLabel>Last seen online</StatLabel>
            <StatNumber>{participant.lastSeenAt ? moment(participant.lastSeenAt).format('DD/MM/YYYY HH:mm') : <Badge>Not seen yet</Badge>}</StatNumber>
          </Stat>
          <Stat mt={2}>
            <StatLabel>Groups</StatLabel>
            <StatNumber mt={2}>{participant.groupObjects?.length > 0
              ? (
                <Box>
                  {participant.groupObjects.map(g =>
                    <Tag size='sm' key={g._id} variant='subtle' bg={g.colour} mr={1} mb={1}>
                      <TagLabel>{g.name}</TagLabel>
                    </Tag>
                  )}
                </Box>)
              : <Badge>No groups</Badge>}
            </StatNumber>
          </Stat>
          <Stat mt={2}>
            <StatLabel>Trial sites</StatLabel>
            <StatNumber mt={2}>{participant.siteObjects?.length > 0
              ? (
                <Box>
                  {participant.siteObjects.map(s =>
                    <Tag size='sm' key={s._id} variant='subtle' mr={1} mb={1}>
                      <TagLabel>{s.name}</TagLabel>
                    </Tag>
                  )}
                </Box>)
              : <Badge>No sites</Badge>}
            </StatNumber>
          </Stat>

          {participant.connectedServices?.length > 0 &&
            <Box mt={5}>
              <Heading size='sm'>Connected services</Heading>
              {participant.connectedServices.filter(s => SERVICES[s.key]).map(s =>
                <Box key={s.key} mt={2} display='flex' alignItems='center'>
                  <Box w='80px' mr={5}>
                    <img src={SERVICES[s.key]?.image} alt={SERVICES[s.key]?.name} />
                  </Box>
                  <Text fontSize='sm'>{SERVICES[s.key]?.name} connected on {moment(s.connectedAt).format('DD/MM/YYYY HH:mm')}</Text>
                </Box>
              )}
            </Box>}
        </Box>
        <Box flex={1}>
          <ParticipantNotes participant={participant} />
        </Box>
      </Box>

      <Box display={{ md: 'flex' }} mt={5}>
        <Box rounded='md' p={3} w='50%' mr={5} bg='gray.50'>
          {utils.canSupportTrial(user, trial) &&
            <ParticipantMessenger trial={trial} participant={participant} />}
        </Box>

        <Box flex={1} bg='gray.50' rounded='md' p={3}>
          <Heading size='md'>Recent activity</Heading>
          <Text mb={5} fontSize='sm'>Actions recently taken by the participant.</Text>
          {!participant?.recentLogs?.length > 0 &&
            <EmptyBox title='Activity feed' description='Entries will appear here once the participant engages with the trial' />}
          <Box overflowY='scroll' maxH='500px'>
            {participant?.recentLogs?.map(log =>
              <Box key={log._id} mb={2} display={{ md: 'flex' }}>
                <Text fontSize='xs' mr={2} w='120px'>{moment(log.createdAt).format('DD/MM/YYYY HH:mm')}</Text>
                <Text fontSize='xs' fontWeight='bold'>{log.type}</Text>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  )
}

function ParticipantEditor ({ trigger, participant, onChange }) {
  const [isOpen, setIsOpen] = useState(false)
  const [password, setPassword] = useState('')
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [whatsapp, setWhatsapp] = useState('')
  const toast = useToast()

  useEffect(() => {
    if (!participant) return
    setPassword('')
    setEmail(participant.email || '')
    setPhone(participant.phone || '')
    setWhatsapp(participant.whatsapp || '')
  }, [participant])

  const savePassword = () => {
    api.trials.updateParticipant(participant.trial, participant._id, { password }, data => {
      onChange({ ...participant, passwordSet: data.passwordSet })
      setIsOpen(false)
    }, err => {
      toast({ title: 'Unable to save password', description: err.message, status: 'error' })
    })
  }

  const saveDetails = () => {
    const data = {
      email: email || null,
      phone: phone || null,
      whatsapp: whatsapp || null
    }
    api.trials.updateParticipant(participant.trial, participant._id, data, d => {
      onChange({ ...participant, ...d })
      setIsOpen(false)
    }, err => {
      toast({ title: 'Unable to save participant', description: err.message, status: 'error' })
    })
  }

  if (!participant) return null
  return (
    <>
      {React.cloneElement(trigger, { onClick: () => setIsOpen(true) })}
      <SimpleModal
        isOpen={isOpen} onClose={() => setIsOpen(false)}
        modalProps={{ size: 'lg' }}
        header={`Edit details for participant ${participant.id}`}
        body={
          <Box>
            <Box p={3} rounded='md' bg='gray.50' mb={5}>
              <Heading size='sm' mb={5}>Trial join & login password</Heading>
              {participant.passwordSet &&
                <Badge colorScheme='green' variant='subtle' mb={5}>A password is currently set</Badge>}
              <FormControl mb={5}>
                <FormLabel>Password</FormLabel>
                <Input bg='white' placeholder='Type a new password...' type='password' value={password || ''} onChange={e => setPassword(e.target.value)} />
                <FormHelperText>Save whilst blank to remove the password</FormHelperText>
              </FormControl>
              <Box display='flex' justifyContent='end'>
                <Button colorScheme='primary' onClick={savePassword}>{password?.length ? 'Save password' : 'Set no password'}</Button>
              </Box>
            </Box>

            <Box p={3} rounded='md' bg='gray.50'>
              <Heading size='sm' mb={5}>Contact details</Heading>
              <FormControl mb={5}>
                <FormLabel>Email address</FormLabel>
                <Input bg='white' placeholder='jane@example.com' type='email' value={email || ''} onChange={e => setEmail(e.target.value)} />
              </FormControl>

              <FormControl mb={5}>
                <FormLabel>Phone number</FormLabel>
                <PhoneInput value={phone} onChange={setPhone} />
              </FormControl>

              <FormControl mb={5}>
                <FormLabel>WhatsApp number</FormLabel>
                <PhoneInput value={whatsapp} onChange={setWhatsapp} isWhatsapp />
              </FormControl>

              <Box display='flex' justifyContent='end'>
                <Button colorScheme='primary' onClick={saveDetails}>Save details</Button>
              </Box>
            </Box>
          </Box>
        }
        footer={
          <Stack justifyContent='end' direction='row'>
            <Button onClick={() => setIsOpen(false)}>Cancel</Button>
          </Stack>
        }
      />
    </>
  )
}
